import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';
import type { GraphQLError } from 'graphql';

import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import { getAutohighlightsQueryVariables } from './getAutohighlightsQueryVariables.ts';

type PreloadAutohighlightsArgs = {
	contentId: string;
	cloudId: string;
	activationId: string;
	contentType: string;
};
// This function is only used for SSR and is being called in the preloadViewPageRoute.ts file. This preloads data that is then fetched from the cache during SSR in the contextual reading aids package -> PageContentRendererFabric component.
export const preloadAutohighlights = ({
	contentId,
	cloudId,
	activationId,
	contentType,
}: PreloadAutohighlightsArgs): Promise<{
	data: unknown;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> => {
	return queryAgg({
		query: preloadAutohighlightsQuery,
		variables: getAutohighlightsQueryVariables({
			contentId,
			cloudId,
			activationId,
			contentType,
		}),
	});
};

export const preloadAutohighlightsQueryFragment: GraphQLTaggedNode = graphql`
	fragment preloadAutohighlightsQuery_query on Query
	@argumentDefinitions(
		entityAri: { type: "String" }
		workspaceId: { type: "String" }
		jiraReporterAccountId: { type: "String" }
		jiraAssigneeAccountId: { type: "String" }
		inputText: { type: "KnowledgeDiscoveryKeyPhraseInputText" }
	) {
		knowledgeDiscovery {
			keyPhrases(
				entityAri: $entityAri
				workspaceId: $workspaceId
				jiraReporterAccountId: $jiraReporterAccountId
				jiraAssigneeAccountId: $jiraAssigneeAccountId
				inputText: $inputText
			) @optIn(to: "KnowledgeDiscovery Get Key Phrases") {
				__typename
				... on KnowledgeDiscoveryKeyPhraseConnection {
					nodes {
						... on KnowledgeDiscoveryKeyPhrase {
							keyPhrase
							category
						}
					}
				}
				... on QueryError {
					message
				}
			}
		}
	}
`;

export const preloadAutohighlightsQuery: GraphQLTaggedNode = graphql`
	query preloadAutohighlightsQuery(
		$entityAri: String!
		$workspaceId: String!
		$jiraReporterAccountId: String
		$jiraAssigneeAccountId: String
		$inputText: KnowledgeDiscoveryKeyPhraseInputText
	) {
		knowledgeDiscovery {
			keyPhrases(
				entityAri: $entityAri
				workspaceId: $workspaceId
				jiraReporterAccountId: $jiraReporterAccountId
				jiraAssigneeAccountId: $jiraAssigneeAccountId
				inputText: $inputText
			) @optIn(to: "KnowledgeDiscovery Get Key Phrases") {
				__typename
				... on KnowledgeDiscoveryKeyPhraseConnection {
					nodes {
						... on KnowledgeDiscoveryKeyPhrase {
							keyPhrase
							category
						}
					}
				}
				... on QueryError {
					message
				}
			}
		}
		...preloadAutohighlightsQuery_query
			@arguments(
				entityAri: $entityAri
				workspaceId: $workspaceId
				jiraReporterAccountId: $jiraReporterAccountId
				jiraAssigneeAccountId: $jiraAssigneeAccountId
				inputText: $inputText
			)
			@alias(as: "autohighlightsQueryFragment")
	}
`;
